/**
 * Next-step suggestion chips — the client half.
 *
 * Muse ends a build turn's closing message with a `<suggestions>[…]</suggestions>`
 * block; the ai service validates it and sends the survivors as message metadata
 * (`metadata.suggestions`, persisted for reload). The raw block still streams as
 * text, so it is hidden here while it types, and the chips render from metadata.
 */

export type SuggestionKind = 'page' | 'section' | 'feature' | 'polish';

export interface Suggestion {
  kind: SuggestionKind;
  label: string;
  prompt: string;
}

const KINDS = new Set<SuggestionKind>(['page', 'section', 'feature', 'polish']);

const BLOCK_RE = /<suggestions>[\s\S]*?(?:<\/suggestions>|$)/gi;
/** A tag still being typed at the very end of the stream: `<`, `<sugg`, `<suggestions`. */
const PARTIAL_TAG_RE = /<(?:s(?:u(?:g(?:g(?:e(?:s(?:t(?:i(?:o(?:n(?:s)?)?)?)?)?)?)?)?)?)?)?$/i;

/** Assistant text without the suggestions block (complete, unclosed, or a tag mid-type). */
export function stripSuggestionsBlock(text: string): string {
  if (!text.includes('<')) {
    return text;
  }

  const stripped = text.replace(BLOCK_RE, '').replace(PARTIAL_TAG_RE, '');

  return stripped === text ? text : stripped.trimEnd();
}

function isSuggestion(value: unknown): value is Suggestion {
  const s = value as Suggestion;

  return (
    !!s &&
    KINDS.has(s.kind) &&
    typeof s.label === 'string' &&
    s.label.trim().length > 0 &&
    typeof s.prompt === 'string' &&
    s.prompt.trim().length > 0
  );
}

interface MessageLike {
  role?: string;
  metadata?: unknown;
}

/**
 * Chips to show: the last message's suggestions, only when it is the assistant's
 * finished reply — once the owner sends anything (a chip or their own words),
 * the previous turn's ideas are gone.
 */
export function latestSuggestions(messages: readonly MessageLike[] | undefined, isStreaming: boolean): Suggestion[] {
  if (isStreaming || !messages?.length) {
    return [];
  }

  const last = messages[messages.length - 1];

  if (last?.role !== 'assistant') {
    return [];
  }

  const items = (last.metadata as { suggestions?: { items?: unknown[] } } | undefined)?.suggestions?.items;

  if (!Array.isArray(items)) {
    return [];
  }

  // Even count (4 or 2): the chips sit in a two-column grid, so a lone chip on a
  // row would break the alignment — older replies saved 3.
  const valid = items.filter(isSuggestion).slice(0, 4);

  return valid.slice(0, valid.length - (valid.length % 2));
}
